import { User, Role } from '../types';
import { store } from '../store';

export type PermissionAction = 'canView' | 'canCreate' | 'canEdit' | 'canDelete' | 'canApprove' | 'canExport';

/**
 * Utility to check if a user has a specific permission for a given module.
 * It strictly mirrors the backend RBAC logic.
 *
 * IMPORTANT: The role's permission is treated as a ceiling. An employee-level
 * override can only narrow access further (e.g. explicitly revoke something
 * the role allows) — it can never grant access to a module/action the
 * employee's role doesn't already permit. This keeps the Role Permissions
 * matrix authoritative: turning something off there guarantees it's off for
 * every employee with that role, regardless of any per-employee override.
 *
 * Only an explicit per-module permission entry (role.permissions[].module ===
 * moduleName) counts toward access. There is no fallback to a top-level
 * field on the role object — a module with no explicit entry is simply not
 * accessible, instead of silently inheriting a stray/legacy top-level flag.
 *
 * @param user The logged-in user object
 * @param rolesList The list of all roles in the system
 * @param moduleName The name of the module (e.g., 'departments', 'employees')
 * @param action The action to check ('canView', 'canEdit', 'canDelete', 'canApprove')
 * @returns boolean indicating if the user has permission
 */
export const checkPermission = (
  user: User | null,
  rolesList: Role[],
  moduleName: string,
  action: PermissionAction,
  employeesList?: any[]
): boolean => {
  if (!user) return false;

  // Admin bypass
  const roleStr = user.role ? (typeof user.role === 'string' ? user.role.toLowerCase() : (user.role as any).name?.toLowerCase() || '') : '';
  if (roleStr === 'admin' || roleStr === 'super admin' || roleStr === 'master admin') return true;

  // 1. Resolve the role's own permission first — this is the ceiling that
  // an employee override is not allowed to exceed.
  const userRoleObj = rolesList.find(r => {
    if (!r) return false;
    const rName = (r.name || "").trim().toLowerCase();

    return (
      rName === roleStr ||
      (user.role && typeof user.role === 'string' && (r._id === user.role || r.id === user.role)) ||
      (user.role && typeof user.role === 'object' && (r._id === (user.role as any)._id || r.id === (user.role as any).id || rName === ((user.role as any).name || "").trim().toLowerCase()))
    );
  });

  // Only an explicit per-module permission entry counts. No fallback to a
  // top-level field on the role object (see note above) — that legacy
  // fallback was silently granting access to every module whenever a role
  // had no explicit entry for that module but happened to have a stray
  // top-level canView/canEdit/etc. field set to true.
  let roleHasPermission = false;
  if (userRoleObj) {
    const rolePermsArr = Array.isArray(userRoleObj.permissions) ? userRoleObj.permissions : [];
    const rolePerms = rolePermsArr.find(p => p.module === moduleName);

    if (rolePerms && typeof rolePerms[action] === 'boolean') {
      roleHasPermission = rolePerms[action] as boolean;
    } else {
      roleHasPermission = false;
    }
  }

  // 2. Employee specific overrides — these can only RESTRICT further, never
  // grant beyond what the role itself allows.
  let userPerms = Array.isArray(user.permissions)
    ? user.permissions.find((p: any) => p.module === moduleName)
    : null;

  const liveEmployees = employeesList || store.getState().data.employees;

  if (liveEmployees) {
    const myEmp = liveEmployees.find(
      (e: any) =>
        e.user?._id === user?._id ||
        e.id === user?.empId ||
        e._id === user?.employeeMongoId ||
        e.user === user?._id,
    );
    if (myEmp && Array.isArray(myEmp.permissions)) {
      const livePerm = myEmp.permissions.find((p: any) => p.module === moduleName);
      if (livePerm) userPerms = livePerm;
    }
  }

  if (userPerms) {
    // An explicit revoke always wins — it narrows access, which is allowed.
    if (userPerms.type === 'revoke') return false;

    // Check expiry
    if (!userPerms.expiry || new Date(userPerms.expiry) >= new Date()) {
      if ((userPerms.type === 'grant' || !userPerms.type) && userPerms[action] === true) {
        // A "grant" override only takes effect if the role already allows
        // this action. If the role has it off, the override cannot turn it
        // back on — the role is the ceiling.
        return roleHasPermission;
      }
    }
  }

  // 3. No override applies — fall back to the role's own permission.
  return roleHasPermission;
};

export const MODULE_PERMISSIONS_MAP: Record<string, string[]> = {
  dashboard: ["canView"],
  reports: ["canView", "canExport"],
  employees: ["canView", "canCreate", "canEdit", "canDelete", "canExport"],
  attendance: ["canView", "canCreate", "canEdit", "canDelete", "canExport"],
  leave: ["canView", "canCreate", "canEdit", "canDelete", "canApprove"],
  timesheets: ["canView", "canCreate", "canEdit", "canDelete", "canApprove"],
  tracking: ["canView"],
  shifts: ["canView", "canCreate", "canEdit", "canDelete"],
  recruitment: ["canView", "canCreate", "canEdit", "canDelete"],
  candidates: ["canView", "canCreate", "canEdit", "canDelete", "canApprove"],
  onboarding: ["canView", "canCreate", "canEdit", "canDelete"],
  performance: ["canView", "canCreate", "canEdit", "canDelete"],
  training: ["canView", "canCreate", "canEdit", "canDelete"],
  projects: ["canView", "canCreate", "canEdit", "canDelete"],
  tasks: ["canView", "canCreate", "canEdit", "canDelete"],
  clients: ["canView", "canCreate", "canEdit", "canDelete"],
  payroll: ["canView", "canCreate", "canEdit", "canDelete", "canApprove", "canExport"],
  invoices: ["canView", "canCreate", "canEdit", "canDelete", "canApprove"],
  companies: ["canView", "canCreate", "canEdit", "canDelete"],
  departments: ["canView", "canCreate", "canEdit", "canDelete"],
  designations: ["canView", "canCreate", "canEdit", "canDelete"],
  hierarchy: ["canView", "canCreate", "canEdit", "canDelete"],
  "company-documents": ["canView", "canCreate", "canEdit", "canDelete", "canExport"],
  chat: ["canView", "canCreate", "canDelete"],
  roles: ["canView", "canCreate", "canEdit", "canDelete"],
  settings: ["canView", "canEdit"],
};